import React from 'react';
import styled from 'styled-components';
import { useLanguage } from '../../contexts/LanguageContext';
import { Twitter, Linkedin, Facebook, Mail } from 'lucide-react';

const FooterContainer = styled.footer`
  width: 100%;
  padding: 48px 0;
  position: relative;
  color: rgba(255, 255, 255, 0.8);
  font-size: 14px;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -5px;
    left: -5px;
    right: -5px;
    bottom: -5px;
    background-image: url(${props => props.$backgroundImage});
    background-size: cover;
    background-position: center;
    opacity: 1;
    filter: blur(8px);
    transform: scale(1.1);
    z-index: 0;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.8);
    z-index: 1;
  }
`;

const FooterContent = styled.div`
  position: relative;
  z-index: 2;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 32px;
  align-items: center;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    text-align: center;
    gap: 48px;
  }
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  &:nth-child(1) {
    align-items: flex-start;
  }

  &:nth-child(2) {
    align-items: center;
  }

  &:nth-child(3) {
    align-items: flex-end;
  }

  @media (max-width: 768px) {
    &:nth-child(1),
    &:nth-child(2),
    &:nth-child(3) {
      align-items: center;
    }
  }
`;

const Copyright = styled.div`
  color: rgba(255, 255, 255, 0.8);
  font-size: 16px;
  transition: color 0.2s ease;

  &:hover {
    color: white;
  }
`;

const BecomeLink = styled.a`
  color: rgba(255, 255, 255, 0.8);
  font-size: 16px;
  text-decoration: none;
  transition: all 0.2s ease;
  padding: 10px 20px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.3);

  &:hover {
    color: white;
    border-color: rgba(255, 255, 255, 0.5);
    transform: translateY(-2px);
    background: rgba(0, 0, 0, 0.5);
  }
`;

const ShareSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`;

const ShareTitle = styled.h3`
  color: white;
  font-size: 18px;
  margin: 0;
  font-weight: 500;
`;

const ShareIcons = styled.div`
  display: flex;
  gap: 16px;
`;

const ShareIcon = styled.a`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(0, 0, 0, 0.5);
    transform: translateY(-2px);
  }
`;

const VersionNumber = styled.div`
  position: absolute;
  bottom: 8px;
  right: 16px;
  font-size: 12px;
  opacity: 0.15;
  color: white;
  z-index: 2;
`;

const Footer = () => {
  const { t, language } = useLanguage();
  const currentYear = new Date().getFullYear();
  const shareUrl = window.location.href;
  const shareText = t('footer.shareText');
  
  return (
    <FooterContainer $backgroundImage={t('footer.backgroundImage')}>
      <FooterContent>
        <Section>
          <Copyright>
            © {currentYear} {t('footer.copyrightText')}
          </Copyright>
        </Section>

        <Section>
          <ShareSection>
            <ShareTitle>{t('footer.shareWithColleagues')}</ShareTitle>
            <ShareIcons>
              <ShareIcon 
                href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={t('footer.shareAria.twitter')}
              >
                <Twitter size={20} />
              </ShareIcon>
              <ShareIcon 
                href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={t('footer.shareAria.linkedin')}
              >
                <Linkedin size={20} />
              </ShareIcon>
              <ShareIcon 
                href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={t('footer.shareAria.facebook')}
              >
                <Facebook size={20} />
              </ShareIcon>
              <ShareIcon 
                href={`mailto:?subject=${encodeURIComponent(shareText)}&body=${encodeURIComponent(shareUrl)}`}
                aria-label={t('footer.shareAria.email')}
              >
                <Mail size={20} />
              </ShareIcon>
            </ShareIcons>
          </ShareSection>
        </Section>

        <Section>
          <BecomeLink 
            href={window.location.pathname === '/mentor' ? '/' : '/mentor'}
            onClick={(e) => {
              e.preventDefault();
              window.location.href = window.location.pathname === '/mentor' ? '/' : '/mentor';
            }}
          >
            {window.location.pathname === '/mentor' ? 'AI Employee' : t('footer.becomeMentor')}
          </BecomeLink>
        </Section>
      </FooterContent>
      {process.env.npm_package_version && (
        <VersionNumber>
          v{process.env.npm_package_version}
        </VersionNumber>
      )}
    </FooterContainer>
  );
};

export default Footer; 